import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { useSendEmailConfirmationMutation } from 'redux/Api/authApi/authApi';
import { IconEyeOpen, IconEyeClosed } from 'pages/Profile/icons';
import s from '../Login/Login.module.scss';
import logo from '../Login/img/logo.svg';
import { ReactComponent as IconCheck } from './img/iconDone.svg';
import { generatePassword } from 'utils/strActions';

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  isTermsAccepted: boolean;
}

interface LocationState {
  phone?: string;
  surname?: string;
  name?: string;
  patronymic?: string;
  email?: string;
  password?: string;
  isTermsAccepted?: boolean;
  fromLogin?: boolean;
}

export const RegistrationStep2: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state as LocationState;

  const [sendEmailConfirmation] = useSendEmailConfirmationMutation();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      email: registrationData?.email || '',
      password: registrationData?.password || '',
      confirmPassword: registrationData?.password || '',
      isTermsAccepted: true,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // useEffect(() => {
  //   if (!registrationData?.password) {
  //     const generatedPassword = generatePassword();
  //     setValue('password', generatedPassword);
  //     setValue('confirmPassword', generatedPassword);
  //   }
  // }, [setValue, registrationData?.password]);

  const password = watch('password');

  const onSubmit = async (data: FormData) => {
    setEmailError('');
    setLoading(true);

    try {
      await sendEmailConfirmation({ email: data.email }).unwrap();
      navigate('/registration/step3', {
        state: {
          ...registrationData,
          email: data.email,
          password: data.password,
          isTermsAccepted: data.isTermsAccepted,
        },
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Ошибка при отправке кода подтверждения на email';
      setEmailError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (value: string) => {
    if (value.length === 0) {
      return 'Пароль должен содержать минимум 6 символов';
    }
    if (value.length < 6) {
      return 'Пароль должен содержать минимум 6 символов';
    }
    if (!/(?=.*[a-z])/.test(value)) {
      return 'Пароль должен содержать хотя бы одну строчную букву';
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return 'Пароль должен содержать хотя бы одну заглавную букву';
    }
    if (!/(?=.*\d)/.test(value)) {
      return 'Пароль должен содержать хотя бы одну цифру';
    }
    return true;
  };

  return (
    <div className={s.enter}>
      <div className={s.inner}>
        <div className={s.logo}>
          <img src={logo} alt='Скилла' />
        </div>

        <h1 className={s.titleStepRegister}>Двухфакторная авторизация</h1>
        <div className={s.stepper}>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItemEmpty}></div>
        </div>

        <div className={s.stepperText}>
          <div className={s.stepperTextItem}>
            <IconCheck />
            <span>Пароль обеспечит защиту твоих средств</span>
          </div>
          <div className={s.stepperTextItem}>
            <IconCheck />
            <span>Восстановить пароль можно по Email</span>
          </div>
        </div>

        <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={s.inputGroup}>
            <label>Задай пароль для входа</label>
            <div className={s.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder='******'
                autoComplete='new-password'
                className={classNames(s.input, errors.password && s.input_error)}
                {...register('password', {
                  required: 'Поле обязательно для заполнения',
                  validate: validatePassword,
                })}
              />
              <button
                type='button'
                className={s.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}>
                {showPassword ? (
                  <IconEyeClosed size={16} color='#ADBFDF' />
                ) : (
                  <IconEyeOpen size={16} color='#ADBFDF' />
                )}
              </button>
            </div>
            {errors.password && (
              <div className={s.errorMessage}>{errors.password.message}</div>
            )}
          </div>

          <div className={s.inputGroup}>
            <label>Повтори пароль</label>
            <div className={s.passwordWrapper}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder='******'
                autoComplete='new-password'
                className={classNames(s.input, errors.confirmPassword && s.input_error)}
                {...register('confirmPassword', {
                  required: 'Поле обязательно для заполнения',
                  validate: (value) => value === password || 'Пароли не совпадают',
                })}
              />
              <button
                type='button'
                className={s.eyeButton}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading}>
                {showConfirmPassword ? (
                  <IconEyeClosed size={16} color='#ADBFDF' />
                ) : (
                  <IconEyeOpen size={16} color='#ADBFDF' />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <div className={s.errorMessage}>{errors.confirmPassword.message}</div>
            )}
          </div>

          <div className={s.inputGroup}>
            <label>Email для восстановления пароля</label>
            <input
              type='email'
              placeholder='@mail.ru'
              autoComplete='off'
              className={classNames(
                s.input,
                (errors.email || emailError) && s.input_error,
              )}
              {...register('email', {
                required: 'Поле обязательно для заполнения',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Введите корректный email адрес',
                },
              })}
            />
            {errors.email && <div className={s.errorMessage}>{errors.email.message}</div>}
            {emailError && <div className={s.errorMessage}>{emailError}</div>}
          </div>

          <button type='submit' className={s.submitButton}>
            Подтвердить Email
          </button>

          <button
            type='button'
            className={s.registerButton}
            onClick={() => navigate('/login')}>
            Начать сначала
          </button>
        </form>

        <div className={s.footer}>
          <span>© {new Date().getFullYear()} go.skilla.ru</span>
        </div>
      </div>
    </div>
  );
};
